/**
 * Admin V3 — Pricing — FixedRoutesEditor
 *
 * In-place editor for Schedule B-A fixed routes (2 canonical B-A routes seeded
 * by SC-OPS-1):
 *   - hotel-france-gare-20:     Hôtel de France → Gare, 20 €
 *   - hotel-france-aeroport-42: Hôtel de France → Aéroport, 42 €
 *
 * SCOPE (Phase 1 binding — owner correction #4):
 *   - Edit-in-place ONLY. Add and Delete are DEFERRED to a future phase.
 *   - Add / Delete buttons are rendered as disabled with a tooltip
 *     explaining the deferral. They never trigger any state mutation.
 *   - service_types is gated to the 3 supported values (standard / corporate /
 *     partner); checkboxes — no priority option.
 *   - bidirectional toggle present per Gate B (default false = unidirectional).
 *
 * Read-only mode: when canEdit=false, all inputs render as disabled.
 */

import React from 'react';
import { Route as RouteIcon, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { SectionCard, cn } from '../../shared/visualTokens';

const labels = {
  title:           'Tarifs forfaitaires (Schedule B-A)',
  emptyHint:       'Aucun tarif forfaitaire défini. Les ajouts sont actuellement désactivés.',
  cols: {
    routeId:       'Identifiant',
    name:          'Nom',
    pickupKw:      'Mots-clés départ',
    dropoffKw:     'Mots-clés arrivée',
    fixedPrice:    'Prix (€)',
    bidirectional: 'Bidirectionnel',
    serviceTypes:  'Classes éligibles',
  },
  addDisabledTip:    'Ajout différé — Phase 2',
  deleteDisabledTip: 'Suppression différée — Phase 2',
  bidirectionalHint: 'Par défaut Gate B = unidirectionnel.',
  hint:            'Les tarifs forfaitaires sont prioritaires sur les bandes pour les itinéraires correspondant à au moins un mot-clé départ ET un mot-clé arrivée.',
};

const SUPPORTED_SERVICE_TYPES = ['standard', 'corporate', 'partner'];

const parseDecimal = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const normalised = String(raw).replace(',', '.').trim();
  const n = Number(normalised);
  return Number.isFinite(n) ? n : NaN;
};

const parseCsvKeywords = (raw) =>
  String(raw || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const Field = ({ label, value, onChange, disabled, error, suffix, testId, placeholder, type = 'text', step, min }) => (
  <div className="flex flex-col">
    <label className="mb-1 block text-[12px] font-semibold text-slate-700">{label}</label>
    <div className="relative">
      <input
        type={type}
        step={step}
        min={min}
        value={value === null || value === undefined ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder || ''}
        className={cn(
          'w-full rounded-xl border bg-white px-3 py-2 pr-10 text-[13px] text-slate-900 outline-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
          error ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-emerald-400',
        )}
        data-testid={testId}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-slate-400">
          {suffix}
        </span>
      )}
    </div>
    {error && (
      <span className="mt-1 text-[11px] text-rose-600" data-testid={`${testId}-error`}>
        {error}
      </span>
    )}
  </div>
);

const ServiceTypesCheckboxes = ({ selected, onChange, disabled, error, baseTestId }) => {
  const arr = Array.isArray(selected) ? selected : [];
  const toggle = (st) => {
    if (disabled) return;
    if (arr.includes(st)) onChange(arr.filter((s) => s !== st));
    else onChange([...arr, st]);
  };
  return (
    <div className="flex flex-col">
      <span className="mb-1 block text-[12px] font-semibold text-slate-700">{labels.cols.serviceTypes}</span>
      <div className="flex flex-wrap gap-2">
        {SUPPORTED_SERVICE_TYPES.map((st) => {
          const isChecked = arr.includes(st);
          return (
            <label
              key={st}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold cursor-pointer transition',
                disabled && 'cursor-not-allowed opacity-60',
                isChecked
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
              )}
              data-testid={`${baseTestId}-st-${st}`}
            >
              <input
                type="checkbox"
                className="h-3 w-3 accent-emerald-600"
                checked={isChecked}
                disabled={disabled}
                onChange={() => toggle(st)}
              />
              {st}
            </label>
          );
        })}
      </div>
      {error && (
        <span className="mt-1 text-[11px] text-rose-600" data-testid={`${baseTestId}-st-error`}>
          {error}
        </span>
      )}
    </div>
  );
};

/**
 * Props:
 *   routes        — array of route objects (controlled)
 *   onChange(next)— receives the updated routes array
 *   errors        — flat error map (dotted paths)
 *   canEdit       — when false, all inputs disabled
 */
const FixedRoutesEditor = ({ routes, onChange, errors, canEdit }) => {
  const safe = Array.isArray(routes) ? routes : [];

  const updateField = (index, field, raw) => {
    const next = safe.map((r, i) => (i === index ? { ...r, [field]: raw } : r));
    onChange(next);
  };

  const updatePrice = (index, raw) => {
    const parsed = parseDecimal(raw);
    updateField(index, 'fixed_price', Number.isNaN(parsed) ? raw : parsed);
  };

  const updateKeywords = (index, field, raw) => {
    updateField(index, field, parseCsvKeywords(raw));
  };

  const updateBidirectional = (index, checked) => {
    updateField(index, 'bidirectional', Boolean(checked));
  };

  const updateServiceTypes = (index, next) => {
    updateField(index, 'service_types', next);
  };

  const err = (k) => (errors && errors[k]) || '';

  return (
    <SectionCard
      title={labels.title}
      icon={RouteIcon}
      iconClass="bg-violet-100 text-violet-600"
      action={
        <div className="flex items-center gap-2">
          {/* Disabled Add — owner correction #4 */}
          <button
            type="button"
            disabled
            title={labels.addDisabledTip}
            className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-[12px] font-semibold text-slate-400"
            data-testid="adminv3-pricing-routes-add-disabled"
          >
            <Plus className="h-3.5 w-3.5" />
            Ajouter
          </button>
        </div>
      }
    >
      <div className="space-y-3" data-testid="adminv3-pricing-fixed-routes-editor">
        {safe.length === 0 && (
          <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-[12.5px] italic text-slate-500">
            {labels.emptyHint}
          </p>
        )}

        {safe.map((r, i) => (
          <div
            key={r?.route_id || `route-${i}`}
            className="rounded-2xl border border-slate-200 bg-white p-4"
            data-testid={`adminv3-pricing-route-row-${r?.route_id || i}`}
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="font-mono text-[12px] text-slate-500">{r?.route_id || '—'}</div>
              {/* Disabled Delete — owner correction #4 */}
              <button
                type="button"
                disabled
                title={labels.deleteDisabledTip}
                className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-[12px] font-semibold text-slate-400"
                data-testid={`adminv3-pricing-route-delete-disabled-${r?.route_id || i}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Supprimer
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                label={labels.cols.name}
                value={r?.name}
                onChange={(v) => updateField(i, 'name', v)}
                error={err(`fixed_routes.${i}.name`)}
                disabled={!canEdit}
                testId={`adminv3-pricing-route-${i}-name`}
              />
              <Field
                label={labels.cols.fixedPrice}
                value={r?.fixed_price}
                onChange={(v) => updatePrice(i, v)}
                error={err(`fixed_routes.${i}.fixed_price`)}
                disabled={!canEdit}
                type="number"
                step="0.01"
                min={0}
                suffix="€"
                testId={`adminv3-pricing-route-${i}-fixed_price`}
              />
              <Field
                label={labels.cols.pickupKw}
                value={Array.isArray(r?.pickup_keywords) ? r.pickup_keywords.join(', ') : ''}
                onChange={(v) => updateKeywords(i, 'pickup_keywords', v)}
                error={err(`fixed_routes.${i}.pickup_keywords`)}
                disabled={!canEdit}
                placeholder="hotel france, hôtel de france"
                testId={`adminv3-pricing-route-${i}-pickup_keywords`}
              />
              <Field
                label={labels.cols.dropoffKw}
                value={Array.isArray(r?.dropoff_keywords) ? r.dropoff_keywords.join(', ') : ''}
                onChange={(v) => updateKeywords(i, 'dropoff_keywords', v)}
                error={err(`fixed_routes.${i}.dropoff_keywords`)}
                disabled={!canEdit}
                placeholder="aéroport, aeroport"
                testId={`adminv3-pricing-route-${i}-dropoff_keywords`}
              />
              <div className="flex flex-col">
                <span className="mb-1 block text-[12px] font-semibold text-slate-700">{labels.cols.bidirectional}</span>
                <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(r?.bidirectional)}
                    onChange={(e) => updateBidirectional(i, e.target.checked)}
                    disabled={!canEdit}
                    className="h-4 w-4 accent-emerald-600"
                    data-testid={`adminv3-pricing-route-${i}-bidirectional`}
                  />
                  <span className="text-[12px] italic text-slate-500">{labels.bidirectionalHint}</span>
                </label>
                {err(`fixed_routes.${i}.bidirectional`) && (
                  <span className="mt-1 text-[11px] text-rose-600">{err(`fixed_routes.${i}.bidirectional`)}</span>
                )}
              </div>
              <ServiceTypesCheckboxes
                selected={r?.service_types}
                onChange={(next) => updateServiceTypes(i, next)}
                disabled={!canEdit}
                error={err(`fixed_routes.${i}.service_types`)}
                baseTestId={`adminv3-pricing-route-${i}`}
              />
            </div>
          </div>
        ))}

        <div className="flex items-start gap-2 rounded-xl bg-violet-50 px-3 py-2.5 text-[12px] italic text-violet-800" data-testid="adminv3-pricing-routes-hint">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>{labels.hint}</span>
        </div>
      </div>
    </SectionCard>
  );
};

export default FixedRoutesEditor;
